import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { DatepickerHeaderComponent } from './datepicker-header.component';

@Component({
  selector: 'app-datepicker-input',
  standalone: true,
  imports: [MatDatepickerModule],
  templateUrl: './datepicker-input.component.html',
  styleUrl: './datepicker-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerInputComponent),
      multi: true,
    },
  ],
})
export class DatepickerInputComponent implements ControlValueAccessor {
  private readonly dateAdapter = inject(DateAdapter<Date>);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly customHeader = DatepickerHeaderComponent;
  readonly placeholder = input('Select date');
  readonly minDate = input<Date | null>(null);
  readonly dateFilter = input<(date: Date | null) => boolean>(() => true);
  readonly hasError = input(false);
  readonly fieldId = input('datepickerInput');
  readonly ariaRequired = input(false);
  readonly ariaInvalid = input(false);

  readonly picker = viewChild.required<MatDatepicker<Date>>('picker');

  readonly value = signal<Date | null>(null);
  disabled = false;

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: Date | null): void {
    this.value.set(value ?? null);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  openPicker(): void {
    if (!this.disabled) {
      this.picker().open();
    }
  }

  onDateChange(event: any): void {
    const date: Date | null = event.value ?? event;
    if (date && !this.isDateAllowed(date)) {
      this.value.set(null);
      this.onChange(null);
    } else {
      this.value.set(date);
      this.onChange(date);
    }
    this.onTouched();
  }

  onInputBlur(): void {
    this.onTouched();
  }

  private isDateAllowed(date: Date): boolean {
    const min = this.minDate();
    if (min && this.dateAdapter.compareDate(date, min) < 0) {
      return false;
    }
    const filter = this.dateFilter();
    if (filter && !filter(date)) {
      return false;
    }
    return true;
  }
}
